import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';

export interface RevealScratchProps {
  children: React.ReactNode; // the content revealed underneath the scratch layer
  className?: string;
  overlayColor?: string;
  hint?: string;
}

const REVEAL_THRESHOLD = 0.55; // fraction of canvas scratched before auto-clearing the rest

/**
 * Scratch-to-reveal overlay: paints a covering layer over `children` that the
 * guest can scratch off with pointer/touch drag (canvas destination-out
 * erasing), revealing the image/content underneath. Falls back to a plain
 * fade-in reveal when reduced motion is requested — scratching is a novelty
 * interaction, not essential content.
 */
export function RevealScratch({ children, className, overlayColor = '#C9A24B', hint = 'Scratch to reveal' }: RevealScratchProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isScratchingRef = useRef(false);
  const scratchedPixelsRef = useRef(0);
  const totalPixelsRef = useRef(1);
  const shouldReduceMotion = useReducedMotion();
  const [revealed, setRevealed] = useState(false);

  const paintOverlay = useCallback((canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = overlayColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [overlayColor]);

  useEffect(() => {
    if (shouldReduceMotion) {
      setRevealed(true);
      return;
    }
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const resize = () => {
      const { width, height } = container.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(width));
      canvas.height = Math.max(1, Math.floor(height));
      totalPixelsRef.current = canvas.width * canvas.height;
      scratchedPixelsRef.current = 0;
      paintOverlay(canvas);
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(container);
    return () => ro.disconnect();
  }, [paintOverlay, shouldReduceMotion]);

  const scratchAt = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas || revealed) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const radius = Math.max(canvas.width, canvas.height) * 0.06;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();

    scratchedPixelsRef.current += Math.PI * radius * radius;
    if (scratchedPixelsRef.current / totalPixelsRef.current > REVEAL_THRESHOLD) {
      setRevealed(true);
    }
  }, [revealed]);

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isScratchingRef.current) return;
    scratchAt(e.clientX, e.clientY);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    isScratchingRef.current = true;
    scratchAt(e.clientX, e.clientY);
  };

  const stopScratching = () => {
    isScratchingRef.current = false;
  };

  return (
    <div ref={containerRef} className={cn('relative w-full h-full overflow-hidden', className)}>
      {children}
      {!revealed && !shouldReduceMotion && (
        <>
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full touch-none cursor-pointer"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={stopScratching}
            onPointerLeave={stopScratching}
          />
          <div className="absolute inset-x-0 bottom-6 flex justify-center pointer-events-none">
            <span className="text-xs md:text-sm uppercase tracking-widest text-white/90 bg-black/30 px-4 py-1.5 rounded-full backdrop-blur-sm">
              {hint}
            </span>
          </div>
        </>
      )}
      {revealed && (
        <motion.div
          initial={shouldReduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'transparent' }}
        />
      )}
    </div>
  );
}
